import { Button, Card, Input } from "@omnichannel/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { ApiError, request } from "./api";
import type { AppRow, UpdateChannel, UpdateChannelPage, UpdateManifest, UpdatePublicationReceipt } from "./api";

const MANIFEST_KEYS = ["app_id", "platform", "channel", "artifact_id", "package_name", "version_code", "version_name", "min_supported_code", "mandatory", "changelog", "artifact_url", "sha256", "signature"] as const;
const MAX_VERSION = 2 ** 31 - 1;
type Scope = Pick<UpdateChannel, "app_id" | "platform" | "channel" | "artifact_id" | "package_name">;

function messageFor(error: unknown, fallback: string) {
  if (!(error instanceof ApiError)) return fallback;
  if (error.status === 403) return "Недостаточно прав владельца для этой операции.";
  if (error.status === 409) return "Конфликт канала или версии. Проверьте текущую публикацию и повторите с теми же данными, если запрос мог завершиться.";
  if (error.status === 422) return `Сервер отклонил данные: ${error.message}`;
  if (error.status === 503) return "Каталог временно недоступен. Повторите тот же запрос без изменения манифеста, причины и канала.";
  return error.message || fallback;
}

function byteLength(value: string) { return new TextEncoder().encode(value).length; }
function hasUnpairedSurrogate(value: string) {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(i + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      i++;
    } else if (code >= 0xdc00 && code <= 0xdfff) return true;
  }
  return false;
}
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function parseManifest(raw: string, scope: Scope, floor: number): { manifest?: UpdateManifest; error?: string } {
  let value: unknown;
  try { value = JSON.parse(raw); } catch { return { error: "Введите корректный JSON подписанного манифеста." }; }
  if (!isPlainObject(value)) return { error: "Манифест должен быть JSON-объектом." };
  const keys = Object.keys(value).sort();
  if (keys.length !== MANIFEST_KEYS.length || keys.some((key, i) => key !== [...MANIFEST_KEYS].sort()[i])) return { error: "Манифест должен содержать только точные поля V2 без пропусков." };
  const m = value as unknown as UpdateManifest;
  if ([scope.app_id, scope.platform, scope.channel, scope.artifact_id, scope.package_name].some((expected, i) => expected !== [m.app_id, m.platform, m.channel, m.artifact_id, m.package_name][i])) return { error: "Область манифеста не совпадает с выбранным неизменяемым каналом." };
  if (typeof m.version_code !== "number" || !Number.isSafeInteger(m.version_code) || m.version_code < 1 || m.version_code > MAX_VERSION) return { error: "version_code должен быть целым числом от 1 до 2³¹−1." };
  if (typeof m.min_supported_code !== "number" || !Number.isSafeInteger(m.min_supported_code) || m.min_supported_code < 0 || m.min_supported_code > m.version_code) return { error: "min_supported_code должен быть целым числом от 0 до version_code." };
  if (typeof m.mandatory !== "boolean") return { error: "mandatory должен быть JSON boolean." };
  if (m.mandatory && m.min_supported_code !== m.version_code) return { error: "Обязательное обновление должно установить порог равным своей версии." };
  if (!m.mandatory && m.min_supported_code !== floor) return { error: "Обычное обновление должно сохранить накопленный порог канала." };
  for (const field of ["version_name", "changelog", "artifact_url", "sha256", "signature"] as const) if (typeof m[field] !== "string") return { error: `${field} должен быть строкой.` };
  if (!m.version_name || byteLength(m.version_name) > 128 || hasUnpairedSurrogate(m.version_name)) return { error: "version_name должен содержать корректный Unicode и быть не длиннее 128 байт UTF-8." };
  if (byteLength(m.changelog) > 65536 || hasUnpairedSurrogate(m.changelog)) return { error: "changelog слишком длинный или содержит некорректный Unicode." };
  if (m.artifact_url.length > 2048 || /[^\x21-\x7e]/.test(m.artifact_url)) return { error: "artifact_url должен быть ASCII адресом HTTPS длиной до 2048 символов." };
  if (!/^https:\/\/[^/?#\\]+/i.test(m.artifact_url)) return { error: "artifact_url должен быть абсолютным HTTPS адресом." };
  try { const url = new URL(m.artifact_url); if (url.protocol !== "https:" || url.username || url.password || m.artifact_url.includes("#") || m.artifact_url.includes("\\")) return { error: "artifact_url должен быть HTTPS без учётных данных, фрагмента и обратной косой черты." }; } catch { return { error: "artifact_url должен быть абсолютным HTTPS адресом." }; }
  if (!/^[0-9a-f]{64}$/.test(m.sha256)) return { error: "sha256 должен содержать 64 строчные шестнадцатеричные цифры." };
  if (!m.signature || m.signature.length > 16384 || /-----BEGIN .*PRIVATE KEY-----/.test(raw)) return { error: "Манифест содержит некорректную подпись или данные закрытого ключа." };
  try { const decoded = atob(m.signature); if (btoa(decoded) !== m.signature || decoded.length < 256 || decoded.length > 1024) return { error: "signature должна быть канонической Base64 подписью RSA." }; } catch { return { error: "signature должна быть Base64." }; }
  return { manifest: m };
}

function receiptMatchesSubmission(value: unknown, manifest: UpdateManifest, reason: string): value is UpdatePublicationReceipt {
  if (!isPlainObject(value)) return false;
  const returnedManifest = value.manifest;
  if (!isPlainObject(returnedManifest)) return false;
  const signedFields = Object.keys(returnedManifest);
  return typeof value.id === "string" && !!value.id
    && typeof value.published_at === "string" && Number.isFinite(Date.parse(value.published_at))
    && value.version_code === manifest.version_code
    && value.reason === reason
    && signedFields.length === MANIFEST_KEYS.length
    && MANIFEST_KEYS.every((field) => returnedManifest[field] === manifest[field]);
}

export function AppUpdates() {
  const [apps, setApps] = useState<AppRow[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [channels, setChannels] = useState<UpdateChannel[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedNeedsReload, setSelectedNeedsReload] = useState<string | null>(null);
  const [channelLoading, setChannelLoading] = useState(true);
  const [appsError, setAppsError] = useState("");
  const [channelError, setChannelError] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [morePending, setMorePending] = useState(false);
  const [provision, setProvision] = useState({ app_id: "", platform: "android", channel: "stable", artifact_id: "", package_name: "", pinned_public_key_b64: "" });
  const [provisionError, setProvisionError] = useState("");
  const [provisionPending, setProvisionPending] = useState(false);
  const [manifestText, setManifestText] = useState("");
  const [reason, setReason] = useState("");
  const [publishError, setPublishError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [receiptsByChannel, setReceiptsByChannel] = useState<Record<string, UpdatePublicationReceipt>>({});
  const [newPublicationFor, setNewPublicationFor] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState("");
  const [refreshPending, setRefreshPending] = useState(false);
  const generation = useRef(0);
  const appsRequest = useRef(0);
  const catalogRequest = useRef(0);
  const morePendingRef = useRef(false);
  const selectionGeneration = useRef(0);
  const draftGeneration = useRef(0);
  const pageCursorByChannel = useRef<Record<string, string | null>>({});
  const selectedIdRef = useRef("");
  const provisionPendingRef = useRef(false);
  const publishingRef = useRef(false);
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const selected = channels.find((channel) => channel.id === selectedId) ?? null;
  const receipt = selected ? receiptsByChannel[selected.id] ?? null : null;
  const editingNewPublication = !!selected && newPublicationFor === selected.id;
  const parsed = useMemo(() => selected ? parseManifest(manifestText, selected, selected.mandatory_floor) : { error: "Сначала выберите канал." }, [manifestText, selected?.id, selected?.mandatory_floor]);

  const loadApps = () => {
    const current = generation.current;
    const requestId = ++appsRequest.current;
    setAppsLoading(true); setAppsError("");
    request<AppRow[]>("/api/v1/apps").then((value) => { if (current === generation.current && requestId === appsRequest.current) setApps(value); }).catch((error) => { if (current === generation.current && requestId === appsRequest.current) setAppsError(messageFor(error, "Не удалось загрузить приложения.")); }).finally(() => { if (current === generation.current && requestId === appsRequest.current) setAppsLoading(false); });
  };

  const firstPageWithSelection = (page: UpdateChannelPage, existing: UpdateChannel[]) => {
    const selectedChannel = existing.find((item) => item.id === selectedIdRef.current);
    return selectedChannel && !page.items.some((item) => item.id === selectedChannel.id)
      ? [...page.items, selectedChannel] : page.items;
  };

  const loadChannels = async (after?: string) => {
    if (after && morePendingRef.current) return;
    const current = generation.current;
    const requestId = after ? catalogRequest.current : ++catalogRequest.current;
    if (after) { morePendingRef.current = true; setMorePending(true); }
    else { morePendingRef.current = false; setChannelLoading(true); setChannelError(""); setMorePending(false); setRefreshPending(false); }
    try {
      const page = await request<UpdateChannelPage>(`/api/v1/app-update-channels?limit=50${after ? `&after=${encodeURIComponent(after)}` : ""}`);
      if (current !== generation.current || requestId !== catalogRequest.current) return;
      for (const item of page.items) pageCursorByChannel.current[item.id] = after ?? null;
      setChannels((existing) => after ? [
        ...existing.map((old) => page.items.find((item) => item.id === old.id) ?? old),
        ...page.items.filter((item) => !existing.some((old) => old.id === item.id)),
      ] : firstPageWithSelection(page, existing));
      if (selectedIdRef.current && page.items.some((item) => item.id === selectedIdRef.current)) setSelectedNeedsReload(null);
      else if (!after && selectedIdRef.current) setSelectedNeedsReload(selectedIdRef.current);
      if (!after && !selectedIdRef.current && page.items[0]) { selectionGeneration.current++; selectedIdRef.current = page.items[0].id; setSelectedId(page.items[0].id); }
      setCursor(page.next_cursor);
    } catch (error) { if (current === generation.current && requestId === catalogRequest.current) setChannelError(messageFor(error, "Не удалось загрузить каналы.")); }
    finally { if (current === generation.current && requestId === catalogRequest.current) { morePendingRef.current = false; setChannelLoading(false); setMorePending(false); } }
  };

  const refreshCatalog = async (selectionAtStart = selectionGeneration.current, channelAtStart = selectedIdRef.current, draftAtStart = draftGeneration.current) => {
    const current = generation.current;
    const requestId = ++catalogRequest.current;
    morePendingRef.current = false;
    setMorePending(false);
    setChannelLoading(true);
    setRefreshPending(true);
    try {
      const page = await request<UpdateChannelPage>("/api/v1/app-update-channels?limit=50");
      if (current === generation.current && requestId === catalogRequest.current) {
        setChannels((existing) => firstPageWithSelection(page, existing));
        for (const item of page.items) pageCursorByChannel.current[item.id] = null;
        setSelectedNeedsReload(selectedIdRef.current && !page.items.some((item) => item.id === selectedIdRef.current) ? selectedIdRef.current : null);
        morePendingRef.current = false;
        setCursor(page.next_cursor); setChannelLoading(false); setMorePending(false);
      }
      if (current === generation.current && requestId === catalogRequest.current && selectionGeneration.current === selectionAtStart && selectedIdRef.current === channelAtStart && draftGeneration.current === draftAtStart) setRefreshError("");
    } catch {
      if (current === generation.current && requestId === catalogRequest.current && selectionGeneration.current === selectionAtStart && selectedIdRef.current === channelAtStart && draftGeneration.current === draftAtStart) setRefreshError("Публикация подтверждена, но список каналов не обновился. Повторите обновление списка.");
    } finally {
      if (current === generation.current && requestId === catalogRequest.current) { setChannelLoading(false); setRefreshPending(false); }
    }
  };

  useEffect(() => {
    generation.current++;
    loadApps();
    void loadChannels();
    const logout = () => {
      generation.current++;
      selectionGeneration.current++;
      selectedIdRef.current = "";
      pageCursorByChannel.current = {};
      // A publication changes verified channel state; the next attempt must reload it.
      provisionPendingRef.current = false;
      publishingRef.current = false;
      morePendingRef.current = false;
      attempt.current = null;
      setApps([]);
      setAppsLoading(false);
      setAppsError("");
      setSelectedId("");
      setSelectedNeedsReload(null);
      setChannels([]);
      setChannelLoading(false);
      setChannelError("");
      setCursor(null);
      setMorePending(false);
      setReceiptsByChannel({});
      setNewPublicationFor(null);
      setPublishing(false);
      setProvisionPending(false);
      setRefreshPending(false);
    };
    window.addEventListener("fleet-hub-unauthorized", logout);
    return () => { generation.current++; window.removeEventListener("fleet-hub-unauthorized", logout); };
  }, []);

  const resetAttempt = () => { draftGeneration.current++; attempt.current = null; setPublishError(""); setRefreshError(""); };
  const handleProvision = async (event: React.FormEvent) => {
    event.preventDefault(); setProvisionError("");
    if (provisionPendingRef.current) return;
    const current = generation.current;
    const selectionAtSubmit = selectionGeneration.current;
    const payload = {
      ...provision,
      platform: provision.platform.trim(),
      channel: provision.channel.trim(),
      artifact_id: provision.artifact_id.trim(),
      package_name: provision.package_name.trim(),
      pinned_public_key_b64: provision.pinned_public_key_b64.trim(),
    };
    if (!payload.app_id || !payload.platform || !payload.channel || !payload.artifact_id || !payload.package_name || !payload.pinned_public_key_b64) { setProvisionError("Заполните все поля канала."); return; }
    for (const [name, limit] of [["platform", 32], ["channel", 32], ["artifact_id", 128]] as const) {
      if (payload[name].length > limit || !/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(payload[name])) { setProvisionError(`${name}: используйте 1–${limit} символов ASCII, начиная с буквы или цифры.`); return; }
    }
    if (payload.package_name.length > 255 || !/^[A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)+$/.test(payload.package_name)) { setProvisionError("package_name должен быть именем пакета с точками и символами ASCII."); return; }
    if (/-----BEGIN .*PRIVATE KEY-----/i.test(payload.pinned_public_key_b64)) { setProvisionError("Нужен открытый RSA ключ в Base64 DER SPKI; закрытый ключ сюда вводить нельзя."); return; }
    let derBytes: Uint8Array<ArrayBuffer>;
    try {
      const raw = atob(payload.pinned_public_key_b64);
      if (btoa(raw) !== payload.pinned_public_key_b64 || raw.length < 1 || raw.length > 2048) throw new Error();
      derBytes = new Uint8Array(new ArrayBuffer(raw.length));
      for (let index = 0; index < raw.length; index++) derBytes[index] = raw.charCodeAt(index);
    } catch { setProvisionError("Публичный ключ должен быть канонической Base64 DER SPKI строкой."); return; }
    if (!globalThis.crypto?.subtle) { setProvisionError("Этот браузер не поддерживает проверку RSA ключа. Откройте панель в безопасном HTTPS контексте."); return; }
    provisionPendingRef.current = true;
    setProvisionPending(true);
    try {
      const key = await globalThis.crypto.subtle.importKey("spki", derBytes, { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" }, false, ["verify"]);
      const algorithm = key.algorithm as RsaHashedKeyAlgorithm;
      if (key.type !== "public" || algorithm.name !== "RSASSA-PKCS1-v1_5" || algorithm.modulusLength < 2048 || algorithm.modulusLength > 8192) throw new Error();
    } catch {
      if (current === generation.current) {
        setProvisionError("Ключ должен быть открытым RSA 2048–8192 бит в DER SubjectPublicKeyInfo (SPKI). Закрытый PKCS#8 ключ не принимается.");
        provisionPendingRef.current = false;
        setProvisionPending(false);
      }
      return;
    }
    if (current !== generation.current) return;
    try {
      const created = await request<UpdateChannel>("/api/v1/app-update-channels", { method: "POST", body: JSON.stringify(payload) });
      if (current !== generation.current) return;
      catalogRequest.current++;
      setChannels((items) => [...items.filter((item) => item.id !== created.id), created].sort((a, b) => a.id.localeCompare(b.id)));
      if (selectionAtSubmit === selectionGeneration.current) {
        selectionGeneration.current++; selectedIdRef.current = created.id; setSelectedId(created.id); setSelectedNeedsReload(null); setManifestText(""); setReason(""); resetAttempt();
      }
      setProvisionError("");
      void loadChannels();
    } catch (error) { if (current === generation.current) setProvisionError(messageFor(error, "Не удалось создать канал.")); }
    finally { if (current === generation.current) { provisionPendingRef.current = false; setProvisionPending(false); } }
  };

  const handlePublish = async () => {
    if (!selected || selectedNeedsReload === selected.id || !parsed.manifest || !reason.trim() || publishingRef.current) return;
    if (receipt && !editingNewPublication) return;
    if (receipt && parsed.manifest.version_code <= receipt.version_code) {
      setPublishError(`Версия должна быть выше уже подтверждённой ${receipt.version_code}. Обновите канал и подготовьте новый подписанный релиз.`);
      return;
    }
    if (reason.length > 1000 || reason.includes("\0") || hasUnpairedSurrogate(reason)) { setPublishError("Причина должна содержать корректный Unicode без NUL, до 1000 символов."); return; }
    if (parsed.manifest.mandatory && !window.confirm(`Подтвердите обязательную публикацию версии ${parsed.manifest.version_name}: все установки ниже ${parsed.manifest.min_supported_code} будут заблокированы и направлены прямо на этот последний артефакт.`)) return;
    const fingerprint = JSON.stringify([selected.id, parsed.manifest, reason]);
    if (!attempt.current || attempt.current.fingerprint !== fingerprint) attempt.current = { fingerprint, key: crypto.randomUUID() };
    const idempotencyKey = attempt.current.key;
    const current = generation.current;
    const channelAtSubmit = selected.id;
    const selectionAtSubmit = selectionGeneration.current;
    const draftAtSubmit = draftGeneration.current;
    publishingRef.current = true;
    setPublishing(true); setPublishError("");
    try {
      const path = `/api/v1/apps/${encodeURIComponent(selected.app_id)}/updates/${encodeURIComponent(selected.platform)}/${encodeURIComponent(selected.channel)}/publications`;
      const result = await request<unknown>(path, { method: "POST", body: JSON.stringify({ manifest: parsed.manifest, reason, idempotency_key: idempotencyKey }) });
      if (current !== generation.current) return;
      if (!receiptMatchesSubmission(result, parsed.manifest, reason)) throw new Error("Publication receipt mismatch");
      setReceiptsByChannel((known) => ({ ...known, [channelAtSubmit]: result }));
      setNewPublicationFor((armed) => armed === channelAtSubmit ? null : armed);
      if (selectionGeneration.current === selectionAtSubmit && selectedIdRef.current === channelAtSubmit) void refreshCatalog(selectionAtSubmit, channelAtSubmit, draftAtSubmit);
    } catch (error) { if (current === generation.current && selectionGeneration.current === selectionAtSubmit && selectedIdRef.current === channelAtSubmit && draftGeneration.current === draftAtSubmit) setPublishError(messageFor(error, "Не удалось подтвердить результат публикации. Повторите без изменения данных, чтобы восстановить ответ.")); }
    finally { if (current === generation.current) { publishingRef.current = false; setPublishing(false); } }
  };

  const handleNewPublication = async () => {
    if (!selected || !receipt || refreshPending) return;
    const current = generation.current;
    const requestId = ++catalogRequest.current;
    const channelId = selected.id;
    const selection = selectionGeneration.current;
    morePendingRef.current = false;
    setMorePending(false);
    setChannelLoading(true);
    setRefreshPending(true); setRefreshError("");
    try {
      const after = pageCursorByChannel.current[channelId];
      const page = await request<UpdateChannelPage>(`/api/v1/app-update-channels?limit=50${after ? `&after=${encodeURIComponent(after)}` : ""}`);
      if (current !== generation.current || requestId !== catalogRequest.current || selection !== selectionGeneration.current || selectedIdRef.current !== channelId) return;
      for (const item of page.items) pageCursorByChannel.current[item.id] = after ?? null;
      const refreshed = page.items.find((item) => item.id === channelId);
      if (!refreshed) {
        setSelectedNeedsReload(channelId);
        setRefreshError("Канал не найден на сохранённой странице. Обновите список и загрузите страницы до выбранного канала.");
        return;
      }
      setChannels((existing) => existing.map((item) => item.id === channelId ? refreshed : item));
      setSelectedNeedsReload(null);
      setNewPublicationFor(channelId);
      setManifestText(""); setReason(""); resetAttempt();
    } catch {
      if (current === generation.current && requestId === catalogRequest.current && selection === selectionGeneration.current && selectedIdRef.current === channelId) setRefreshError("Не удалось проверить актуальное состояние канала. Повторите перед созданием нового манифеста.");
    } finally {
      if (current === generation.current && requestId === catalogRequest.current) { setChannelLoading(false); setRefreshPending(false); }
    }
  };

  return (
    <main className="shell">
      <div className="shell-header"><h1>Обновления приложений</h1></div>
      <p>Канал фиксирует публичный ключ и область выпуска. Подписанный манифест подготовьте вне Fleet Hub; сервер проверяет подпись перед публикацией.</p>

      <Card className="person-section">
        <h2>Каналы обновлений</h2>
        {appsError && <p role="alert" className="error-text">{appsError} <Button onClick={loadApps}>Повторить загрузку приложений</Button></p>}
        {channelError && <p role="alert" className="error-text">{channelError} <Button onClick={() => void loadChannels()}>Повторить</Button></p>}
        {channelLoading && <p role="status">Загрузка каналов…</p>}
        {!channelLoading && !channelError && channels.length === 0 && <p>Каналов пока нет. Создайте канал ниже.</p>}
        {channels.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead><tr><th>Продукт / UUID</th><th>Область</th><th>Артефакт / пакет</th><th>Публичный pin</th><th>Порог</th><th>Релиз</th></tr></thead>
              <tbody>{channels.map((item) => (
                <tr key={item.id}>
                  <td>{apps.find((app) => app.id === item.app_id)?.name ?? item.app_id}<br /><code>{item.app_id}</code></td>
                  <td>{item.platform} · {item.channel}</td>
                  <td>{item.artifact_id}<br />{item.package_name}</td>
                  <td><code>{item.pinned_public_key_b64}</code></td>
                  <td>{item.mandatory_floor}</td>
                  <td>{item.has_release ? "опубликован" : "нет релиза"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        {cursor && <Button variant="secondary" disabled={morePending || channelLoading} onClick={() => void loadChannels(cursor)}>{morePending ? "Загрузка…" : "Загрузить ещё"}</Button>}
        {channels.length > 0 && (
          <div className="field">
            <label htmlFor="update-channel">Выбранный канал</label>
            <select id="update-channel" value={selectedId} onChange={(event) => {
              selectionGeneration.current++;
              selectedIdRef.current = event.target.value;
              setSelectedId(event.target.value);
              setNewPublicationFor(null);
              setRefreshError("");
              setManifestText("");
              setReason("");
              resetAttempt();
            }}>
              {channels.map((item) => <option key={item.id} value={item.id}>{apps.find((app) => app.id === item.app_id)?.name ?? item.app_id} · {item.platform}/{item.channel} · {item.artifact_id}</option>)}
            </select>
          </div>
        )}
      </Card>

      <Card className="person-section">
        <h2>Создать неизменяемый канал</h2>
        {appsLoading && <p role="status">Загрузка приложений…</p>}
        {!appsLoading && !appsError && apps.length === 0 && <p>Сначала создайте приложение в разделе «Продукты».</p>}
        <form onSubmit={handleProvision} className="update-form">
          <fieldset disabled={provisionPending} className="update-fields">
            <label>Продукт
              <select value={provision.app_id} onChange={(event) => setProvision({ ...provision, app_id: event.target.value })}>
                <option value="">Выберите приложение</option>
                {apps.map((app) => <option key={app.id} value={app.id}>{app.name} · {app.id}</option>)}
              </select>
            </label>
            {([
              ["platform", "Платформа"],
              ["channel", "Канал"],
              ["artifact_id", "Идентификатор артефакта"],
              ["package_name", "Идентификатор пакета"],
              ["pinned_public_key_b64", "Открытый RSA ключ · Base64 DER SPKI"],
            ] as const).map(([name, label]) => (
              <label key={name}>{label}
                {name === "pinned_public_key_b64"
                  ? <textarea rows={3} value={provision[name]} onChange={(event) => setProvision({ ...provision, [name]: event.target.value })} />
                  : <Input value={provision[name]} onChange={(event) => setProvision({ ...provision, [name]: event.target.value })} />}
              </label>
            ))}
          </fieldset>
          <p>Только открытый RSA ключ в каноническом Base64 DER SPKI. Ключ распространяется клиентам отдельно; заменить или сбросить pin через эту панель нельзя.</p>
          {provisionError && <p role="alert" className="error-text">{provisionError}</p>}
          <Button className="primary" type="submit" disabled={provisionPending}>{provisionPending ? "Создание…" : "Создать канал"}</Button>
        </form>
      </Card>

      {selected && <Card className="person-section">
        <h2>Опубликовать подписанный манифест</h2>
        {selectedNeedsReload === selected.id && <p role="status">Данные выбранного канала устарели после обновления списка. Загрузите следующие страницы до этого канала, прежде чем готовить новую публикацию.</p>}
        {receipt && (
          <div className="api-key-reveal" role="status">
            <strong>Последняя подтверждённая публикация</strong>
            <p>Версия {receipt.manifest.version_name} ({receipt.version_code}), запись {receipt.id}, {new Date(receipt.published_at).toLocaleString("ru-RU")}.</p>
            <p>Квитанция сохранена в памяти страницы и остаётся видимой при изменении черновика или выборе другого канала.</p>
          </div>
        )}
        {refreshError && <p role="alert" className="error-text">{refreshError} <Button variant="secondary" disabled={refreshPending} onClick={() => void refreshCatalog()}>{refreshPending ? "Обновление…" : "Повторить обновление списка"}</Button></p>}
        {receipt && !editingNewPublication && <Button className="primary" disabled={refreshPending || selectedNeedsReload === selected.id} onClick={() => void handleNewPublication()}>{refreshPending ? "Проверка канала…" : "Новая публикация"}</Button>}
        {(!receipt || editingNewPublication) && <>
          {receipt && <p role="status">Канал обновлён с сервера. Новый релиз должен иметь версию выше подтверждённой {receipt.version_code}; квитанция выше сохранена.</p>}
          <label htmlFor="update-manifest">Подписанный манифест V2 · JSON</label>
          <textarea id="update-manifest" rows={12} value={manifestText} onChange={(event) => { setManifestText(event.target.value); resetAttempt(); }} />
          {parsed.error && manifestText && <p className="error-text" role="alert">{parsed.error}</p>}
          {receipt && parsed.manifest && parsed.manifest.version_code <= receipt.version_code && <p role="alert" className="error-text">Новая публикация должна иметь версию выше уже подтверждённой {receipt.version_code}.</p>}
          {parsed.manifest && (
            <div className="update-preview">
              <h3>Предпросмотр</h3>
              <p>Продукт: {apps.find((app) => app.id === selected.app_id)?.name ?? selected.app_id} · {selected.platform}/{selected.channel}</p>
              <p>Версия: {parsed.manifest.version_name} ({parsed.manifest.version_code})</p>
              <p>Режим: {parsed.manifest.mandatory ? "обязательное" : "обычное, можно отложить"}</p>
              <p>Порог: {parsed.manifest.min_supported_code} (текущий накопленный: {selected.mandatory_floor})</p>
              <p>Цель: последний артефакт напрямую — {parsed.manifest.artifact_url}</p>
              <p>SHA-256: <code>{parsed.manifest.sha256}</code></p>
              {parsed.manifest.mandatory && <p className="error-text">Обязательное обновление блокирует все версии ниже порога.</p>}
            </div>
          )}
          <label htmlFor="update-reason">Причина публикации (обязательна)</label>
          <textarea id="update-reason" rows={3} maxLength={1000} value={reason} onChange={(event) => { setReason(event.target.value); resetAttempt(); }} />
          {publishError && <p className="error-text" role="alert">{publishError}</p>}
          <Button className="primary" disabled={!parsed.manifest || !reason.trim() || publishing || (!!receipt && (!editingNewPublication || parsed.manifest.version_code <= receipt.version_code)) || selectedNeedsReload === selected.id} onClick={() => void handlePublish()}>{publishing ? "Публикация…" : "Проверить и опубликовать"}</Button>
          <p>При неопределённом сетевом результате повтор использует тот же ключ запроса, пока манифест, причина и канал не изменены. После перезагрузки страницы этот ключ не сохраняется.</p>
        </>}
      </Card>}
    </main>
  );
}
